import axios from 'axios';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { showToast, saveAuthTokens } from '../../utill/utill';

export default function AdminLogin() {
    const accessToken = sessionStorage.getItem("access_token");
    const navigate = useNavigate();

    useEffect(() => {
        if (accessToken) {
            navigate('/gf-admin/monitoring');
        }
    }, [accessToken, navigate]);

    const [formData, setFormData] = useState({
        email: '',
        password: '',
    });
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isGoogleLoading, setIsGoogleLoading] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({
        email: '',
        password: ''
    });
    const googleButtonRef = useRef(null);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (error) {
            setError(null);
        }
        if (fieldErrors[name]) {
            setFieldErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const getErrorMessage = (error) => {
        if (!error.response) {
            return '네트워크 연결을 확인해주세요.';
        }

        const status = error.response.status;
        const data = error.response.data;

        switch (status) {
            case 400:
                return data.detail || '입력한 정보를 확인해주세요.';
            case 401:
                return data.detail || '이메일 또는 비밀번호가 올바르지 않습니다.';
            case 403:
                return data.detail || '접근 권한이 없습니다.';
            case 422:
                if (data.detail && Array.isArray(data.detail)) {
                    const errors = {};
                    data.detail.forEach(err => {
                        if (err.loc && err.loc.length > 1) {
                            const field = err.loc[1];
                            errors[field] = err.msg;
                        }
                    });
                    setFieldErrors(prev => ({ ...prev, ...errors }));
                    return '입력한 정보를 확인해주세요.';
                }
                return data.detail || '입력한 정보가 올바르지 않습니다.';
            case 429:
                return '너무 많은 요청이 발생했습니다. 잠시 후 다시 시도해주세요.';
            case 500:
            case 502:
            case 503:
                return '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
            default:
                return data.detail || data.message || '로그인 중 오류가 발생했습니다.';
        }
    };

    const verifyAdminRole = async (accessToken) => {
        const userResponse = await axios.get(`${process.env.REACT_APP_API_URL}/user/my`, {
            headers: { Authorization: `Bearer ${accessToken}` },
        });
        return userResponse.data.default_role === 'admin';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setFieldErrors({ email: '', password: '' });
        setIsLoading(true);

        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/user/login`, {
                email: formData.email,
                password: formData.password,
            });

            await saveAuthTokens({ ...response.data, email: formData.email });

            const isAdmin = await verifyAdminRole(response.data.access_token);
            if (!isAdmin) {
                sessionStorage.clear();
                setError("관리자 권한이 없는 계정입니다.");
                return;
            }

            navigate('/gf-admin/monitoring');
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            setError(errorMessage);

            if (error.response?.status === 401) {
                setTimeout(() => {
                    document.getElementById('admin-password')?.focus();
                }, 100);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleGoogleCredential = useCallback(async (response) => {
        setIsGoogleLoading(true);
        setError(null);
        try {
            const res = await axios.post(`${process.env.REACT_APP_API_URL}/user/auth/google`, {
                credential: response.credential,
            });
            await saveAuthTokens(res.data);

            const isAdmin = await verifyAdminRole(res.data.access_token);
            if (!isAdmin) {
                sessionStorage.clear();
                setError('관리자 권한이 없는 계정입니다.');
                showToast('관리자 권한이 없는 계정입니다.', 'error');
                return;
            }

            navigate('/gf-admin/monitoring');
        } catch (err) {
            const errorMessage = err.response?.data?.detail || err.response?.data?.message || err.message || '구글 로그인 중 오류가 발생했습니다.';
            setError(errorMessage);
            showToast(errorMessage, 'error');
        } finally {
            setIsGoogleLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        const renderGoogleButton = () => {
            const container = googleButtonRef.current;
            if (!window.google?.accounts?.id || !container) return false;
            window.google.accounts.id.initialize({
                client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
                callback: handleGoogleCredential,
                auto_select: false,
                itp_support: true,
            });
            container.innerHTML = '';
            const width = container.closest('.admin-login-google-wrapper')?.offsetWidth || 328;
            window.google.accounts.id.renderButton(container, {
                type: 'standard',
                theme: 'filled_black',
                size: 'large',
                text: 'signin_with',
                shape: 'rectangular',
                logo_alignment: 'left',
                width: Math.floor(width),
                locale: 'ko',
            });
            return true;
        };

        if (!renderGoogleButton()) {
            const checkGoogle = setInterval(() => {
                if (renderGoogleButton()) clearInterval(checkGoogle);
            }, 100);
            return () => clearInterval(checkGoogle);
        }

        const handleResize = () => renderGoogleButton();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [handleGoogleCredential]);

    return (
        <div className="admin-login-body">
            <div className="admin-login-card">
                <div className="admin-login-header">
                    <div className="admin-login-brand">GrowFit</div>
                    <div className="admin-login-badge">Admin</div>
                </div>
                <p className="admin-login-subtitle">관리자 계정으로 로그인하세요</p>

                <form onSubmit={handleSubmit}>
                    {error && (
                        <div className="admin-login-error">
                            <strong>로그인 실패</strong>
                            <span>{error}</span>
                        </div>
                    )}

                    <div className="admin-login-field">
                        <label htmlFor="admin-email">이메일</label>
                        <input
                            type="email"
                            id="admin-email"
                            name="email"
                            className={fieldErrors.email ? 'error' : ''}
                            placeholder="admin@growfit.io"
                            required
                            autoComplete="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            disabled={isLoading || isGoogleLoading}
                        />
                        {fieldErrors.email && (
                            <div className="admin-login-field-error">{fieldErrors.email}</div>
                        )}
                    </div>

                    <div className="admin-login-field">
                        <label htmlFor="admin-password">비밀번호</label>
                        <input
                            type="password"
                            id="admin-password"
                            name="password"
                            className={fieldErrors.password ? 'error' : ''}
                            placeholder="비밀번호를 입력하세요"
                            required
                            autoComplete="current-password"
                            value={formData.password}
                            onChange={handleInputChange}
                            disabled={isLoading || isGoogleLoading}
                        />
                        {fieldErrors.password && (
                            <div className="admin-login-field-error">{fieldErrors.password}</div>
                        )}
                    </div>

                    <button
                        type="submit"
                        className={`admin-login-button ${isLoading ? 'loading' : ''}`}
                        disabled={isLoading || isGoogleLoading}
                    >
                        {isLoading ? '로그인 중...' : '관리자 로그인'}
                    </button>
                </form>

                <div className="admin-login-divider">
                    <div className="admin-login-divider-line"></div>
                    <span className="admin-login-divider-text">또는</span>
                    <div className="admin-login-divider-line"></div>
                </div>

                <div className="admin-login-google-wrapper">
                    <div ref={googleButtonRef} className="admin-login-google-btn"></div>
                    {isGoogleLoading && (
                        <div className="admin-login-google-loading">
                            <span>구글 로그인 중...</span>
                        </div>
                    )}
                </div>

                <button
                    type="button"
                    className="admin-login-home-button"
                    onClick={() => navigate('/')}
                    disabled={isLoading || isGoogleLoading}
                >
                    Home으로 돌아가기
                </button>

                <div className="admin-login-footer">
                    <span>GrowFit Platform Administration</span>
                </div>
            </div>
        </div>
    );
}
